import { ModalComponentProps } from "Components/General/Modal/ModalContext";
import * as React from "react";
import createEngine, {
  DefaultLinkModel,
  DefaultNodeModel,
  DefaultNodeModelOptions,
  DiagramEngine,
  DiagramModel,
} from "@projectstorm/react-diagrams";
import { CanvasWidget } from "@projectstorm/react-canvas-core";
import {
  FlowChartResponse,
  FlowChartStep,
  workflowActionStore,
  workflowChartStore,
} from "Pages/Project/Workflow/Stores/WorkflowStep";
import { Button } from "Components/General/Button";
import { useEffect, useState } from "react";
import { Col, Row, Space, Switch } from "antd";
import { uniqueId } from "lodash";
import { DiamondNodeFactory } from "./FlowChartDiagram/DiamondNodeFactory";
import { DiamondNodeModel } from "./FlowChartDiagram/DiamondNodeModel";
import { CustomNodeFactory } from "./FlowChartDiagram/CustomNodeFactory";
import { CustomNodeModel } from "./FlowChartDiagram/CustomNodeModel";
import { AdvancedLinkFactory } from "./FlowChartDiagram/AdvancedLinkFactory";
import { AdvancedPortModel } from "./FlowChartDiagram/AdvancedPortModel";
import { useTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { workflowsStore } from "Pages/Admin/Stores/WorkflowsStore";
import { userInfoStore } from "Stores";
import { Loader } from "Components/General/Loader";
import classnames from "classnames";

interface FlowchartModal extends ModalComponentProps {
  scheme?: FlowChartResponse;
  name?: string;
  description?: string;
  definitionXml?: string;
  adminProjectId?: number;
}

interface PortEntriesData {
  in?: { port?: AdvancedPortModel; points?: number[] };
  out?: { port?: AdvancedPortModel; points?: number[] };
}

const engine: DiagramEngine = createEngine();
export const FlowchartModal: React.FC<FlowchartModal> = ({ adminProjectId, definitionXml }) => {
  const { t } = useTranslation(I18N_NS.PROJECT);
  const [loading, setLoading] = useState<boolean>(true);
  const [initialized, setInitialized] = useState<boolean>(false);
  const [fullChart, setFullChart] = useState<boolean>(false);
  const [activeScheme, setActiveScheme] = useState<FlowChartResponse>(null);
  const [model, setModel] = useState<DiagramModel>(new DiagramModel());
  const positionCoords: { x: number; y: number } = { x: 0, y: 0 };
  const FAKE_STEP_ID = -999; // Filter for 'fake' steps
  const STEP_CELL_WIDTH = 500; // Width of grid cell for one step (node + arrow line)
  const MARGIN_Y_END_STEP = 300; // Y axis margin
  const MARGIN_Y_STEP = MARGIN_Y_END_STEP; // do not to get confused in the names
  const MARGIN_Y_STEPS = 120; // space between nodes levels (Y axis)
  const DECISION_STEP_WIDTH = 300;
  const SIMPLE_STEP_WIDTH = 230;
  const SYSTEM_STEP_BG = "#fff";
  const HUMAN_STEP_BG = "#fed330";
  const DECISION_STEP_BG = "#d3d3d3";
  const END_STEP_BG = DECISION_STEP_BG;
  const CURRENT_STEP_BG = "#0fb9b1";

  /* Connect custom components */
  engine.getLinkFactories().registerFactory(new AdvancedLinkFactory());
  engine.getNodeFactories().registerFactory(new DiamondNodeFactory());
  engine.getNodeFactories().registerFactory(new CustomNodeFactory());

  /* nodes = {step id, step node with custom options (to interact with them later)} */
  const nodes: Record<number, DefaultNodeModel | DiamondNodeModel> = {};
  /* ports = {step id, data with all in/out ports for certain step node } */
  const ports: Record<number, PortEntriesData> = {};

  const isFakeStep = (id: number | string) => {
    return `${id}`.substring(0, 4) === `${FAKE_STEP_ID}`;
  };

  const createStepNode = (
    step: FlowChartStep,
    coords?: { x?: number; y?: number },
    options?: Record<string, unknown>
  ) => {
    if (isFakeStep(step.stepId)) {
      /* Creation of the END step node */
      const deepYLvl = activeScheme.grid?.[1];
      const CURRENT_POS_X = step.xy[0] * STEP_CELL_WIDTH;
      const CURRENT_POS_Y = step.isAlone ? step.xy[1] * MARGIN_Y_END_STEP : deepYLvl * -MARGIN_Y_END_STEP;
      const nodeOptions: DefaultNodeModelOptions = {
        name: "End",
        color: END_STEP_BG,
        extras: {
          id: step.stepId,
          type: "end",
          position: {
            x: CURRENT_POS_X,
            y: CURRENT_POS_Y,
          },
          label: [{ id: step.stepId, action: step.action }],
          ...options,
        },
      };
      const node = new CustomNodeModel(nodeOptions);

      ports[step.stepId] = { in: {}, out: {} };
      const portIn = node.addPort(new AdvancedPortModel(true, "In"));
      ports[step.stepId].in = { port: portIn as AdvancedPortModel, points: [] };
      portIn.getOptions().locked = true;
      nodes[step.stepId] = node;
      model.addNode(node);
    } else {
      /* Creation of the other steps node */
      const STEP_RENDER_TYPE = step.actionType === "GW_DECISION" ? "decision" : "simple";
      const CURRENT_POS_X = step.xy[0] * STEP_CELL_WIDTH;
      const STEP_BG_COLOR = step.currentStep
        ? CURRENT_STEP_BG
        : step.actionType.includes("GW_")
        ? DECISION_STEP_BG
        : step.stepType === "HUMANTASK"
        ? HUMAN_STEP_BG
        : SYSTEM_STEP_BG;
      const validNextSteps = step.nextSteps;
      const fullTitle = `${step.title && step.title} ${step.name && `(${step.name})`}`;

      const nodeOptions: DefaultNodeModelOptions = {
        name: step.title,
        id: `${step.stepId}`,
        color: STEP_BG_COLOR,
        extras: {
          id: step.stepId,
          type: STEP_RENDER_TYPE,
          position: {
            x: CURRENT_POS_X,
            y: step.xy[1] * MARGIN_Y_STEP,
          },
          fullTitle,
          label: validNextSteps,
          ...options,
        },
      };
      const node = new CustomNodeModel(nodeOptions);

      ports[step.stepId] = { in: {}, out: {} };
      const portIn = node.addPort(new AdvancedPortModel(true, "In"));
      const portOut = node.addPort(new AdvancedPortModel(false, "Out"));
      portIn.getOptions().locked = true;
      portOut.getOptions().locked = true;

      ports[step.stepId].in = { port: portIn as AdvancedPortModel, points: [] };
      if (validNextSteps.length) {
        ports[step.stepId].out = { port: portOut as AdvancedPortModel, points: validNextSteps.map((v) => v.id) };
      }
      options?.parenActionType !== "GW_DECISION" && (positionCoords.x = CURRENT_POS_X);
      positionCoords.x = CURRENT_POS_X;
      nodes[step.stepId] = node;
      model.addNode(node);
    }
  };

  const createLinks = () => {
    let deepYLvl = 30;

    for (const key in ports) {
      if (Object.prototype.hasOwnProperty.call(ports, key)) {
        ports[key].out.points?.length &&
          ports[key].out.points.slice().forEach((p) => {
            const outputPort = ports[key].out.port;
            const link = outputPort.link<DefaultLinkModel>(ports[p].in.port);
            const targetPort = link.getTargetPort();
            const SPACE_WIDTH =
              outputPort.getParent().getOptions().extras?.type === "decision" ? DECISION_STEP_WIDTH : SIMPLE_STEP_WIDTH;
            const NODE_HEIGHT = 80 - 2;
            const ADDITIONAL_SPACE_WIDTH = 50;

            /* Create link with points to node that already defined previously in the flow (check the X axis pos).
             Draw line below current Y level
             Example: [1] -> [2] -> [3] ->[4].
             Step [3] has two links: [4] and [1]. Draw link line from [3] back to [1].
           */
            if (targetPort.getX() < outputPort.getX()) {
              link.point(targetPort.getX() - ADDITIONAL_SPACE_WIDTH, targetPort.getY() + NODE_HEIGHT);
              link.point(targetPort.getX() - ADDITIONAL_SPACE_WIDTH, outputPort.getY() + MARGIN_Y_STEPS + deepYLvl);
              link.point(
                outputPort.getX() + SPACE_WIDTH + ADDITIONAL_SPACE_WIDTH,
                outputPort.getY() + MARGIN_Y_STEPS + deepYLvl
              );
              link.point(outputPort.getX() + SPACE_WIDTH + ADDITIONAL_SPACE_WIDTH, outputPort.getY() + NODE_HEIGHT);
              deepYLvl += 100;
            }
            if (targetPort.getY() > outputPort.getY()) {
              link.point(outputPort.getX() + SPACE_WIDTH, targetPort.getY());
            }

            /* Create additional link point to end step node (Y axis pos) */
            if (targetPort.getParent().getOptions().extras?.type === "end") {
              const yPosAdd = targetPort.getParent().getOptions().extras?.isAlone ? NODE_HEIGHT / 2 : NODE_HEIGHT;
              link.point(outputPort.getX() + SPACE_WIDTH, targetPort.getY() + yPosAdd);
            }
            const label = outputPort
              .getParent()
              .getOptions()
              .extras?.label.find((lb) => lb.id === p)?.action;
            label && link.addLabel(label);
            link.getOptions().curvyness = 0;
            // isFakeStep(p) && link.setColor('red');
            model.addLink(link);
          });
      }
    }
  };

  const createPosition = () => {
    let deepAxisLvlX = 0;
    let isPrevStepEnd = true;
    for (const key in nodes) {
      if (nodes[key].getOptions().extras?.position) {
        const { x, y } = nodes[key].getOptions().extras?.position || {};
        const parentId = nodes[key].getOptions().extras?.parentId;
        const isEndStep = `${nodes[key].getOptions().extras?.id}`?.includes(`${FAKE_STEP_ID}`);
        const xPos = x;
        const yPos = y;
        if (isEndStep && parentId) {
          isPrevStepEnd && (deepAxisLvlX += 1);
          isPrevStepEnd = isEndStep;
        } else {
          isPrevStepEnd = false;
        }
        nodes[key].setPosition(xPos, yPos);
      }
    }
  };

  const drawChart = async (
    id: number | string,
    coords?: { x?: number; y?: number },
    step?: {
      id?: string | number;
      action?: string;
      parentId?: string | number;
      parenActionType?: API_WORKFLOWFLOWCHARTOBJECT_TYPE;
      xy?: number[];
    }
  ) => {
    if (isFakeStep(id)) {
      /**
       * Render a fake step with additional data which not provided by BE
       */
      createStepNode({ ...step, stepId: id }, { x: coords.x, y: activeScheme.grid[1] + 1 }, step);
    } else {
      /**
       * Render a normal step
       */
      const currentStep = activeScheme.steps[id] as FlowChartStep;
      const withChildren = currentStep.nextSteps.length;

      /**
       * Set the uniq id for each fake step (-999)
       */
      if (currentStep.nextSteps.length) {
        for (const stp of currentStep.nextSteps) {
          if (stp.id === FAKE_STEP_ID) {
            stp.id = uniqueId(`${stp.id}_`);
          }
        }
      }

      createStepNode(currentStep, coords, step);

      /**
       * Render children of the certain step
       */
      if (withChildren) {
        for (const v of currentStep.nextSteps) {
          if (!nodes[v.id]) {
            const newId = v.id;
            const data = {
              ...v,
              parentId: currentStep.stepId,
              parenActionType: currentStep.actionType,
              xy: [currentStep.xy[0] + 1, currentStep.xy[1]],
              isAlone: withChildren === 1,
            };
            await drawChart(newId, positionCoords, data);
          }
        }
      }
    }
  };

  const createNodes = async (scheme: FlowChartResponse) => {
    await drawChart(scheme.firstStepId, positionCoords);
    createPosition();
    createLinks();
    engine.maxNumberPointsPerLink = 0;
    model.setLocked(true);
    engine.setModel(model);
    /**
     * To render complete canvas without timeout does not work
     * need to fix
     * "loading" prop for <Canvas /> render
     */
    setTimeout(() => {
      setLoading(false);
    }, 100);
  };

  const handleZoom = (v: "IN" | "OUT") => {
    let newZoom: number = model.getZoomLevel();
    if (v === "IN") {
      newZoom = newZoom + 5;
    } else {
      newZoom = newZoom - 5;
    }
    model.setZoomLevel(newZoom);
    engine.setModel(model);
  };

  const fetchFlowchart = async (
    withFilter: boolean = false,
    definitionXml: string = null,
    adminProjectId: number = null
  ) => {
    try {
      setInitialized(false);
      setLoading(true);
      setModel(new DiagramModel());
      if (definitionXml) {
        await workflowChartStore.fetchMock(workflowsStore.projectId, definitionXml);
      } else {
        const wfId: number = adminProjectId || workflowActionStore.data.workflowInfo.id;
        const filter = withFilter;
        const prId = adminProjectId ? workflowsStore.projectId : undefined;
        await workflowChartStore.fetch(wfId, filter, prId);
      }
      setActiveScheme(workflowChartStore.flowchart);
    } catch (error) {
      setLoading(false);
      throw error;
    }
  };

  useEffect(() => {
    (async () => {
      /**
       * If definitionXml, will means mock (preview) mode
       * If adminProjectId, will means admin mode
       */
      await fetchFlowchart(!fullChart, definitionXml, adminProjectId);
    })();
  }, [fullChart]);

  useEffect(() => {
    activeScheme && createNodes(activeScheme);
  }, [activeScheme]);

  useEffect(() => {
    /**
     * To render complete canvas without timeout does not work
     * need to fix
     * "initialized" to hide <Loader />
     */
    if (!loading) {
      setTimeout(() => {
        engine.zoomToFitNodes({});
        setInitialized(true);
      }, 100);
    }
  }, [loading]);

  return (
    <div className="flex-column flowchart-container" style={{ height: "100%" }}>
      <Row className="wf-title" justify="space-between">
        <Col flex={1} className="mb-md">
          <h1>{workflowChartStore?.flowchart?.wfName}</h1>
        </Col>
      </Row>
      <Row className="wf-title" justify="space-between">
        <Col flex={1}>{workflowChartStore?.flowchart?.wfDescription}</Col>
        <Col>
          <Space size="large">
            <Button onClick={() => engine.zoomToFitNodes({})} size="small">
              {t("Fit to window")}
            </Button>
            <div>
              <span className="mr-sm">{t("Zoom")}:</span>
              <Button faIcon="minus" onClick={() => handleZoom("OUT")} size="small" />
              <Button faIcon="plus" onClick={() => handleZoom("IN")} size="small" />
            </div>
            {!definitionXml && userInfoStore.isSysAdmin && (
              <div>
                {t("Full view")}:
                <Switch className="ml-sm" onChange={(checked) => setFullChart(checked)} />
              </div>
            )}
          </Space>
        </Col>
      </Row>
      {<Loader className={classnames("absoluteBoxLoader", { nonVisible: initialized })} />}
      {!loading && <CanvasWidget engine={engine} className="flowchart fullHeight" />}
    </div>
  );
};
